import { z } from 'zod';
import { usernameError } from '../auth';
import { FINANCE_KINDS } from '../finance';
import { MAC_RE } from '../mac';
import {
  activityType,
  dbMoney,
  dbNumeric,
  dbQty,
  dbSignedMoney,
  dbUnitPrice,
  isoDate,
  jsonValue,
  movementReason,
  nonEmptyTrimmed,
  pgTime,
  role,
  stockLocation,
  taskProgress,
  timestamptz,
  unit,
  uuid,
  vatRate,
} from './common';

// Check-constraint enums (packages/db/supabase/migrations/20260905000100_schema.sql)
export const ATTENDANCE_SOURCES = ['unifi', 'simulated', 'manual'] as const;
export const REQUEST_STATUSES = ['waiting', 'ordered', 'arrived', 'closed'] as const;
export const REQUEST_SOURCES = ['manual', 'whatsapp'] as const;
export const DEBT_ITEM_KINDS = ['stock', 'manual'] as const;
export const SALE_ITEM_KINDS = ['stock', 'manual', 'history'] as const;
export const DELIVERY_STATUSES = ['pending', 'done'] as const;
export const LEAVE_TYPES = ['annual', 'medical', 'unpaid', 'other'] as const;
export const LEAVE_STATUSES = ['pending', 'approved', 'rejected'] as const;
export const LEAVE_SEASONS = ['summer', 'winter'] as const;
export const LEAVE_BLOCKS = [1, 2] as const;
export const LEAVE_SCHEMES = ['10+4', '7+7'] as const;
export const SCHEDULE_ROLES = ['core', 'backup'] as const;
export const SALE_SOURCES = ['shop', 'warehouse'] as const;
export const TASK_PRIORITIES = ['normal', 'high'] as const;
export const TASK_REPEAT_RULES = ['none', 'daily', 'weekly', 'monthly'] as const;
export const TASK_STATUSES = ['pending', 'progress', 'done'] as const;
export const WA_CONVERSATION_STATUSES = ['open', 'closed'] as const;
export const WA_DIRECTIONS = ['in', 'out'] as const;
export const WA_MESSAGE_STATUSES = ['received', 'queued', 'sent', 'delivered', 'read', 'failed'] as const;

export const attendanceSource = z.enum(ATTENDANCE_SOURCES);
export const requestStatus = z.enum(REQUEST_STATUSES);
export const requestSource = z.enum(REQUEST_SOURCES);
export const debtItemKind = z.enum(DEBT_ITEM_KINDS);
export const saleItemKind = z.enum(SALE_ITEM_KINDS);
export const deliveryStatus = z.enum(DELIVERY_STATUSES);
export const leaveType = z.enum(LEAVE_TYPES);
export const leaveStatus = z.enum(LEAVE_STATUSES);
export const leaveSeason = z.enum(LEAVE_SEASONS);
export const leaveBlock = z.union([z.literal(1), z.literal(2)]);
export const leaveScheme = z.enum(LEAVE_SCHEMES);
export const scheduleRole = z.enum(SCHEDULE_ROLES);
export const saleSource = z.enum(SALE_SOURCES);
export const taskPriority = z.enum(TASK_PRIORITIES);
export const taskRepeatRule = z.enum(TASK_REPEAT_RULES);
export const taskStatus = z.enum(TASK_STATUSES);
export const waConversationStatus = z.enum(WA_CONVERSATION_STATUSES);
export const waDirection = z.enum(WA_DIRECTIONS);
export const waMessageStatus = z.enum(WA_MESSAGE_STATUSES);
export const financeKind = z.enum(FINANCE_KINDS);

const nonNegativeInt = z.number().int().nonnegative();
const macOrEmpty = z.string().refine((v) => v === '' || MAC_RE.test(v));
const usernameColumn = z.string().refine((u) => usernameError(u) === null);
const waPhone = z.string().regex(/^\+[0-9]{6,15}$/);

export const activityRow = z.object({
  created_at: timestamptz,
  detail: z.string(),
  id: uuid,
  text: z.string(),
  type: activityType,
  user_id: uuid.nullable(),
  user_name: z.string(),
  user_role: z.string(),
});

export const appSettingRow = z.object({
  key: z.string(),
  updated_at: timestamptz,
  value: z.string(),
});

export const attendanceDayRow = z
  .object({
    created_at: timestamptz,
    date: isoDate,
    employee_id: uuid,
    first_seen: timestamptz.nullable(),
    id: uuid,
    last_seen: timestamptz.nullable(),
    source: attendanceSource,
    summary: jsonValue.nullable(),
    updated_at: timestamptz,
  })
  .refine(
    (r) =>
      r.first_seen === null || r.last_seen === null || Date.parse(r.last_seen) >= Date.parse(r.first_seen),
    { path: ['last_seen'] },
  );

export const attendanceSettingsRow = z.object({
  auto_sync: z.boolean(),
  endpoint: z.string(),
  id: z.literal(1),
  last_sync: timestamptz.nullable(),
  rotation: jsonValue,
  rotation_start_date: isoDate,
  simulate_enabled: z.boolean(),
  sunday_workers_count: nonNegativeInt,
  updated_at: timestamptz,
});

export const barcodeBatchRow = z.object({
  created_at: timestamptz,
  created_by: uuid.nullable(),
  id: uuid,
  labels: jsonValue,
  purchase_id: uuid.nullable(),
  title: z.string(),
  total_labels: nonNegativeInt,
});

export const customerRequestRow = z.object({
  created_at: timestamptz,
  created_by: uuid.nullable(),
  customer_name: nonEmptyTrimmed,
  date: isoDate,
  id: uuid,
  note: z.string(),
  phone: z.string(),
  product_id: uuid.nullable(),
  product_name: nonEmptyTrimmed,
  qty: dbQty,
  source: requestSource,
  status: requestStatus,
  updated_at: timestamptz,
  wa_conversation_id: uuid.nullable(),
});

export const customerRow = z.object({
  /** NEW — a removed card that still has history: off the list, kept for the reports. */
  archived_at: timestamptz.nullable(),
  created_at: timestamptz,
  first_name: nonEmptyTrimmed,
  id: uuid,
  last_name: z.string(),
  national_id: z.string(),
  phone: z.string(),
  place: z.string(),
  updated_at: timestamptz,
});

export const debtItemRow = z.object({
  debt_id: uuid,
  id: uuid,
  kind: debtItemKind,
  name: nonEmptyTrimmed,
  position: z.number().int(),
  price: dbUnitPrice,
  product_id: uuid.nullable(),
  qty: dbQty,
});

export const debtRow = z
  .object({
    created_at: timestamptz,
    created_by: uuid.nullable(),
    customer_id: uuid,
    date: isoDate,
    due: isoDate,
    id: uuid,
    note: z.string(),
    total: dbMoney,
  })
  .refine((r) => r.due >= r.date, { path: ['due'] });

export const deliveryRow = z
  .object({
    /** NEW — where the goods go, typed at the till. */
    address: z.string(),
    created_at: timestamptz,
    customer: z.string(),
    done_at: timestamptz.nullable(),
    done_by: uuid.nullable(),
    id: uuid,
    /** NEW — where the worker takes the goods from. */
    location: z.enum(['local', 'warehouse']),
    note: nonEmptyTrimmed,
    /** NEW — "Porosia #1024". */
    number: z.number().int().positive(),
    sale_id: uuid,
    status: deliveryStatus,
    worker_employee_id: uuid.nullable(),
    worker_name: z.string(),
  })
  .refine((r) => (r.status === 'done') === (r.done_at !== null), { path: ['done_at'] });

export const employeeLeaveRow = z
  .object({
    block: leaveBlock.nullable(),
    created_at: timestamptz,
    days: nonNegativeInt,
    employee_id: uuid,
    from_date: isoDate,
    id: uuid,
    note: z.string(),
    outside_season: z.boolean(),
    season: leaveSeason.nullable(),
    status: leaveStatus,
    to_date: isoDate,
    type: leaveType,
    updated_at: timestamptz,
  })
  .refine((r) => r.to_date >= r.from_date, { path: ['to_date'] })
  .refine((r) => r.type !== 'annual' || r.from_date.slice(0, 4) === r.to_date.slice(0, 4), {
    path: ['to_date'],
  })
  .refine((r) => r.type !== 'annual' || (r.season !== null && r.block !== null), { path: ['season'] });

export const employeeRow = z
  .object({
    annual_leave_days: nonNegativeInt,
    annual_leave_scheme: leaveScheme,
    created_at: timestamptz,
    device: z.string(),
    id: uuid,
    mac: macOrEmpty,
    name: nonEmptyTrimmed,
    phone: z.string(),
    roster_key: z.string(),
    schedule_role: scheduleRole,
    shift_end: pgTime,
    shift_start: pgTime,
    updated_at: timestamptz,
  })
  .refine((r) => r.shift_end > r.shift_start, { path: ['shift_end'] });

export const financeEntryRow = z.object({
  amount: dbMoney,
  created_at: timestamptz,
  created_by: uuid.nullable(),
  date: isoDate,
  id: uuid,
  kind: financeKind,
  note: z.string(),
  updated_at: timestamptz,
});

export const paymentRow = z.object({
  amount: dbMoney.pipe(z.number().positive()),
  created_at: timestamptz,
  created_by: uuid.nullable(),
  customer_id: uuid,
  date: isoDate,
  id: uuid,
  note: z.string(),
});

export const presenceEventRow = z.object({
  employee_id: uuid.nullable(),
  id: uuid,
  mac: z.string(),
  online: z.boolean(),
  raw: jsonValue,
  seen_at: timestamptz,
});

export const productRow = z.object({
  /** NEW — set when the product was deleted; it stays for the documents that name it. */
  archived_at: timestamptz.nullable(),
  buy: dbUnitPrice,
  code: nonEmptyTrimmed,
  code2: z.string(),
  created: isoDate,
  created_at: timestamptz,
  id: uuid,
  imported: z.boolean(),
  margin: dbNumeric,
  name: nonEmptyTrimmed,
  sell: dbUnitPrice,
  unit,
  updated_at: timestamptz,
});

export const profileRow = z.object({
  active: z.boolean(),
  created_at: timestamptz,
  employee_id: uuid.nullable(),
  id: uuid,
  name: nonEmptyTrimmed,
  role,
  updated_at: timestamptz,
  username: usernameColumn,
});

export const purchaseDraftRow = z.object({
  payload: jsonValue,
  updated_at: timestamptz,
  user_id: uuid,
});

export const purchaseItemRow = z.object({
  buy: dbUnitPrice,
  code: z.string(),
  id: uuid,
  labels: nonNegativeInt,
  line_net: dbMoney,
  line_total: dbMoney,
  margin: dbNumeric,
  name: z.string(),
  position: z.number().int(),
  product_id: uuid,
  purchase_id: uuid,
  qty: dbQty,
  sell: dbUnitPrice,
  unit,
  vat: vatRate,
  vat_amount: dbMoney,
});

/**
 * Official totals (subtotal/vat_total/total/vat8/vat18) are CHECK ≥ 0; calc_* are computed from the
 * lines by save_purchase; vat*_net/vat*_gross are the invoice reference values (OCR) and are unchecked.
 */
export const purchaseRow = z.object({
  calc_subtotal: dbMoney,
  calc_total: dbMoney,
  calc_vat_total: dbMoney,
  created_at: timestamptz,
  created_by: uuid.nullable(),
  date: isoDate,
  /** NEW — the invoice discount: the amount it took off the net, and the percentage (0 ≤ x < 100). */
  discount: dbMoney,
  discount_pct: dbMoney.refine((pct) => pct < 100),
  file_name: z.string(),
  id: uuid,
  invoice_text: z.string(),
  number: nonEmptyTrimmed,
  review_required: z.boolean(),
  subtotal: dbMoney,
  supplier_id: uuid,
  total: dbMoney,
  totals_detected: z.boolean(),
  vat_total: dbMoney,
  vat0_gross: dbSignedMoney,
  vat0_net: dbSignedMoney,
  vat18: dbMoney,
  vat18_gross: dbSignedMoney,
  vat18_net: dbSignedMoney,
  vat8: dbMoney,
  vat8_gross: dbSignedMoney,
  vat8_net: dbSignedMoney,
});

export const pushSubscriptionRow = z.object({
  auth: z.string(),
  created_at: timestamptz,
  employee_id: uuid.nullable(),
  endpoint: z.string().url(),
  id: uuid,
  last_used_at: timestamptz.nullable(),
  p256dh: z.string(),
  user_agent: z.string(),
  user_id: uuid,
});

export const saleItemRow = z.object({
  code: z.string(),
  cost: dbUnitPrice,
  id: uuid,
  kind: saleItemKind,
  name: nonEmptyTrimmed,
  position: z.number().int(),
  price: dbUnitPrice,
  product_id: uuid.nullable(),
  qty: dbQty,
  sale_id: uuid,
  unit: z.string(),
});

/** NEW — goods given back from today's sales (return_sale_item). */
export const saleReturnRow = z.object({
  amount: dbMoney,
  code: z.string(),
  created_at: timestamptz,
  created_by: uuid.nullable(),
  id: uuid,
  kind: saleItemKind,
  name: nonEmptyTrimmed,
  price: dbUnitPrice,
  product_id: uuid.nullable(),
  qty: dbQty,
  sale_id: uuid,
});

export const saleRow = z.object({
  client_sale_id: uuid.nullable(),
  created_at: timestamptz,
  created_by: uuid.nullable(),
  id: uuid,
  ref: z.string().nullable(),
  printed: z.boolean(),
  sold_at: timestamptz,
  source: saleSource,
  total: dbMoney,
});

export const stockMovementRow = z.object({
  created_at: timestamptz,
  created_by: uuid.nullable(),
  id: uuid,
  location: stockLocation,
  product_id: uuid,
  qty_delta: dbNumeric.pipe(z.number().refine((n) => n !== 0)),
  reason: movementReason,
  ref_id: uuid.nullable(),
  ref_table: z.string(),
});

export const supplierRow = z.object({
  address: z.string(),
  created_at: timestamptz,
  fiscal: z.string(),
  id: uuid,
  name: nonEmptyTrimmed,
  phone: z.string(),
  updated_at: timestamptz,
});

export const taskProgressHistoryRow = z.object({
  created_at: timestamptz,
  employee_id: uuid.nullable(),
  id: uuid,
  progress: taskProgress,
  task_id: uuid,
  user_name: z.string(),
});

export const taskRow = z
  .object({
    assignee_employee_id: uuid.nullable(),
    completed_at: timestamptz.nullable(),
    completed_by_employee_id: uuid.nullable(),
    created_at: timestamptz,
    created_by: uuid.nullable(),
    description: z.string(),
    due_date: isoDate,
    due_time: pgTime.nullable(),
    group_id: uuid,
    id: uuid,
    previous_task_id: uuid.nullable(),
    priority: taskPriority,
    progress: taskProgress,
    repeat_rule: taskRepeatRule,
    spawned_next: z.boolean(),
    started_at: timestamptz.nullable(),
    status: taskStatus,
    title: nonEmptyTrimmed,
    updated_at: timestamptz,
  })
  .refine((r) => (r.status === 'done') === (r.completed_at !== null), { path: ['completed_at'] });

export const waConversationRow = z.object({
  created_at: timestamptz,
  id: uuid,
  last_inbound_at: timestamptz.nullable(),
  last_message_at: timestamptz.nullable(),
  name: z.string(),
  phone: waPhone,
  status: waConversationStatus,
  unread: nonNegativeInt,
  updated_at: timestamptz,
});

export const waMessageRow = z.object({
  body: z.string(),
  conversation_id: uuid,
  created_at: timestamptz,
  created_by: uuid.nullable(),
  direction: waDirection,
  id: uuid,
  provider_message_id: z.string().nullable(),
  raw: jsonValue,
  sent_at: timestamptz,
  status: waMessageStatus,
});

// Views
export const customerBalanceRow = z.object({
  balance: dbMoney.nullable(),
  customer_id: uuid.nullable(),
  debts: dbMoney.nullable(),
  payments: dbMoney.nullable(),
});

export const productStockRow = z.object({
  local: dbNumeric.nullable(),
  product_id: uuid.nullable(),
  total: dbNumeric.nullable(),
  warehouse: dbNumeric.nullable(),
});

export const productWithStockRow = z.object({
  buy: dbUnitPrice.nullable(),
  code: z.string().nullable(),
  code2: z.string().nullable(),
  created: isoDate.nullable(),
  created_at: timestamptz.nullable(),
  id: uuid.nullable(),
  imported: z.boolean().nullable(),
  local: dbNumeric.nullable(),
  margin: dbNumeric.nullable(),
  name: z.string().nullable(),
  sell: dbUnitPrice.nullable(),
  total: dbNumeric.nullable(),
  unit: unit.nullable(),
  updated_at: timestamptz.nullable(),
  warehouse: dbNumeric.nullable(),
});

/** Every table/view row schema keyed by its Postgres name. */
export const ROW_SCHEMAS = {
  activities: activityRow,
  app_settings: appSettingRow,
  attendance_days: attendanceDayRow,
  attendance_settings: attendanceSettingsRow,
  barcode_batches: barcodeBatchRow,
  customer_requests: customerRequestRow,
  customers: customerRow,
  debt_items: debtItemRow,
  debts: debtRow,
  deliveries: deliveryRow,
  employee_leaves: employeeLeaveRow,
  employees: employeeRow,
  finance_entries: financeEntryRow,
  payments: paymentRow,
  presence_events: presenceEventRow,
  products: productRow,
  profiles: profileRow,
  purchase_drafts: purchaseDraftRow,
  purchase_items: purchaseItemRow,
  purchases: purchaseRow,
  push_subscriptions: pushSubscriptionRow,
  sale_items: saleItemRow,
  sale_returns: saleReturnRow,
  sales: saleRow,
  stock_movements: stockMovementRow,
  suppliers: supplierRow,
  task_progress_history: taskProgressHistoryRow,
  tasks: taskRow,
  wa_conversations: waConversationRow,
  wa_messages: waMessageRow,
  customer_balances: customerBalanceRow,
  product_stock: productStockRow,
  products_with_stock: productWithStockRow,
} as const;

export type ActivityRow = z.infer<typeof activityRow>;
export type AppSettingRow = z.infer<typeof appSettingRow>;
export type AttendanceDayRow = z.infer<typeof attendanceDayRow>;
export type AttendanceSettingsRow = z.infer<typeof attendanceSettingsRow>;
export type BarcodeBatchRow = z.infer<typeof barcodeBatchRow>;
export type CustomerRequestRow = z.infer<typeof customerRequestRow>;
export type CustomerRow = z.infer<typeof customerRow>;
export type DebtItemRow = z.infer<typeof debtItemRow>;
export type DebtRow = z.infer<typeof debtRow>;
export type DeliveryRow = z.infer<typeof deliveryRow>;
export type EmployeeLeaveRow = z.infer<typeof employeeLeaveRow>;
export type EmployeeRow = z.infer<typeof employeeRow>;
export type PaymentRow = z.infer<typeof paymentRow>;
export type PresenceEventRow = z.infer<typeof presenceEventRow>;
export type ProductRow = z.infer<typeof productRow>;
export type ProfileRow = z.infer<typeof profileRow>;
export type PurchaseDraftRow = z.infer<typeof purchaseDraftRow>;
export type PurchaseItemRow = z.infer<typeof purchaseItemRow>;
export type PurchaseRow = z.infer<typeof purchaseRow>;
export type PushSubscriptionRow = z.infer<typeof pushSubscriptionRow>;
export type SaleItemRow = z.infer<typeof saleItemRow>;
export type SaleReturnRow = z.infer<typeof saleReturnRow>;
export type SaleRow = z.infer<typeof saleRow>;
export type StockMovementRow = z.infer<typeof stockMovementRow>;
export type SupplierRow = z.infer<typeof supplierRow>;
export type TaskProgressHistoryRow = z.infer<typeof taskProgressHistoryRow>;
export type TaskRow = z.infer<typeof taskRow>;
export type WaConversationRow = z.infer<typeof waConversationRow>;
export type WaMessageRow = z.infer<typeof waMessageRow>;
export type CustomerBalanceRow = z.infer<typeof customerBalanceRow>;
export type ProductStockRow = z.infer<typeof productStockRow>;
export type ProductWithStockRow = z.infer<typeof productWithStockRow>;
